import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  closestCenter,
  CollisionDetection,
  DndContext as DndKitContext,
  DragOverlay,
  getFirstCollision,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { DroppableContainer } from './components/DropableContainer';
import { DndContextProps, DndProps, Items, UseDndProps } from './types';
import { SortableItem } from './components/SortableItem';
import { createPortal } from 'react-dom';
import { dropAnimation } from './utils';
import { Item } from './components';
import { coordinateGetter } from './utils/multipleContainersKeyboardCoordinates';

export const useDnd = ({
  items: initialItems = {},
}: UseDndProps | undefined = {}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [clonedItems, setClonedItems] = useState<Items | null>(null);
  const [items, setItems] = useState<Items>(initialItems ?? null);
  const [containers, setContainers] = useState(
    Object.keys(items) as UniqueIdentifier[],
  );
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    }),
  );

  const findContainer = useCallback(
    (id: UniqueIdentifier) => {
      if (items.hasOwnProperty(id)) return id;

      return Object.keys(items).find((key) => items[key].includes(id));
    },
    [items],
  );

  const onDragStart = useCallback(
    ({ active }: any) => {
      setActiveId(active.id);
      setClonedItems(items);
    },
    [items],
  );

  const onDragOver = useCallback(
    ({ active, over }: any) => {
      const overId = over?.id;

      if (overId == null || items.hasOwnProperty(active.id)) {
        return;
      }

      const overContainer = findContainer(overId);
      const activeContainer = findContainer(active.id);

      if (!overContainer || !activeContainer) {
        return;
      }

      if (activeContainer !== overContainer) {
        setItems((items) => {
          const activeItems = items[activeContainer];
          const overItems = items[overContainer];
          const overIndex = overItems.indexOf(overId);
          const activeIndex = activeItems.indexOf(active.id);

          if (activeIndex < 0 || overIndex < 0) return { ...items };
          let newIndex: number;

          if (items.hasOwnProperty(overId)) {
            newIndex = overItems.length + 1;
          } else {
            const isBelowOverItem =
              over &&
              active.rect.current.translated &&
              active.rect.current.translated.top >
                over.rect.top + over.rect.height;

            const modifier = isBelowOverItem ? 1 : 0;

            newIndex =
              overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
          }

          recentlyMovedToNewContainer.current = true;

          const newItems = {
            ...items,
            [activeContainer]: items[activeContainer].filter(
              (item) => item !== active.id,
            ),
            [overContainer]: [
              ...items[overContainer].slice(0, newIndex),
              items[activeContainer][activeIndex],
              ...items[overContainer].slice(
                newIndex,
                items[overContainer].length,
              ),
            ],
          };

          return newItems;
        });
      }
    },
    [findContainer, items, setItems],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: any, onDragEnd: Function) => {
      if (items.hasOwnProperty(active.id) && over?.id) {
        onDragEnd({
          container: active.id,
          newContainer: over?.id,
        });
      }

      const activeContainer = findContainer(active.id);

      if (!activeContainer) {
        setActiveId(null);
        return;
      }

      const overId = over?.id;

      if (overId == null) {
        setActiveId(null);
        return;
      }

      const overContainer = findContainer(overId);

      if (overContainer) {
        const activeIndex = items[activeContainer].indexOf(active.id);
        const overIndex = items[overContainer].indexOf(overId);

        if (activeIndex !== overIndex) {
          setItems((items) => ({
            ...items,
            [overContainer]: arrayMove(
              items[overContainer],
              activeIndex,
              overIndex,
            ),
          }));

          onDragEnd({
            container: overContainer,
            activeIndex,
            newIndex: overIndex,
          });
        }
      }

      setActiveId(null);
    },
    [findContainer, items],
  );

  const onDragCancel = useCallback(() => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  }, [clonedItems]);

  const renderSortableItemDragOverlay = useCallback(
    (
      id: UniqueIdentifier,
      renderDraggableItem: (dndProps: DndProps) => React.ReactElement,
    ) => {
      return <Item value={id} renderItem={renderDraggableItem} dragOverlay />;
    },
    [],
  );

  const renderContainerDragOverlay = useCallback(
    (
      containerId: UniqueIdentifier,
      renderDraggleContainer: (dndProps: DndProps) => React.ReactElement,
    ) => {
      return renderDraggleContainer({
        dragOverlay: true,
        value: containerId,
      });
    },
    [],
  );

  const renderOverlay = useCallback(
    (
      activeId: UniqueIdentifier | null,
      containers: UniqueIdentifier[],
      renderDraggableItem: (dndProps: any) => React.ReactElement,
      renderDraggleLevel: (dndProps: any) => React.ReactElement,
    ) => {
      if (activeId) {
        return containers.includes(activeId)
          ? renderContainerDragOverlay(activeId, renderDraggleLevel)
          : renderSortableItemDragOverlay(activeId, renderDraggableItem);
      }
      return null;
    },
    [renderContainerDragOverlay, renderSortableItemDragOverlay],
  );

  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && items.hasOwnProperty(activeId)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) =>
            items.hasOwnProperty(container.id),
          ),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (items.hasOwnProperty(overId)) {
          const containerItems = items[overId];

          // If a container is matched and it contains items
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId &&
                  containerItems.includes(container.id),
              ),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items],
  );

  useEffect(() => {
    const initialItemsKeys = Object.keys(initialItems);
    if (initialItemsKeys.length) {
      setItems(initialItems);
      setContainers(initialItemsKeys as UniqueIdentifier[]);
    }

    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [initialItems]);

  const DndContext = ({
    children,
    renderDraggableItem,
    renderDraggleContainer,
    onDragEnd,
  }: PropsWithChildren<DndContextProps>) => {
    return (
      <DndKitContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={({ active, over }: any) => {
          return handleDragEnd({ active, over }, onDragEnd);
        }}
        modifiers={[restrictToWindowEdges]}
        autoScroll={{
          layoutShiftCompensation: { x: false, y: true },
          threshold: { x: 0, y: 0 },
        }}
        // cancelDrop={cancelDrop}
        onDragCancel={onDragCancel}
      >
        <SortableContext
          items={[...containers]}
          strategy={verticalListSortingStrategy}
        >
          {children}
        </SortableContext>
        {createPortal(
          <DragOverlay adjustScale={false} dropAnimation={dropAnimation}>
            {activeId &&
              renderOverlay(
                activeId,
                containers,
                renderDraggableItem,
                renderDraggleContainer,
              )}
          </DragOverlay>,
          document.body,
        )}
      </DndKitContext>
    );
  };

  return {
    SortableItem,
    DndContext,
    DroppableContainer,
    SortableContext,
  };
};

export function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}
